import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Images, Eye, EyeOff, Trash2, Download, Loader2, CheckSquare, Square, QrCode, Clock, CheckCircle, RotateCcw, ChevronDown, ChevronUp, Mail, Plus, X, Play } from 'lucide-react';
import { toast } from 'sonner';
import {
  getAdminPhotos, updatePhotoSettings, updatePhotoStatus, deleteWeddingPhoto,
  bulkPhotoAction, adminPhotosZipUrl, photosQrUrl, photoFileUrl, getAdminMe,
  getPhotosTrash, restoreWeddingPhoto, getAlbumEmails, addAlbumEmail, deleteAlbumEmail,
} from '@/lib/api';
import { Input } from '@/components/ui/input';

const STATUS_LABELS = {
  published: { label: 'Publiée', className: 'bg-green-100 text-green-800' },
  pending: { label: 'En attente', className: 'bg-amber-100 text-amber-800' },
  hidden: { label: 'Masquée', className: 'bg-stone-200 text-stone-600' },
};

const StatCard = ({ label, value, icon: Icon }) => (
  <Card className="card-elegant !p-0">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-terracotta" />
      </div>
      <div>
        <p className="text-2xl font-semibold text-charcoal leading-none">{value}</p>
        <p className="text-xs text-stone mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const AlbumEmailsCard = () => {
  const [emails, setEmails] = useState([]);
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getAlbumEmails().then((res) => setEmails(res.data.emails || [])).catch(() => {});
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      const res = await addAlbumEmail(newEmail);
      setEmails((prev) => [...prev, res.data].sort((a, b) => a.email.localeCompare(b.email)));
      setNewEmail('');
      toast.success('Email ajouté à la liste');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (entry) => {
    try {
      await deleteAlbumEmail(entry.id);
      setEmails((prev) => prev.filter((x) => x.id !== entry.id));
      toast.success(`${entry.email} retiré — il ne peut plus consulter l'album`);
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Card className="card-elegant !p-0">
      <button
        className="w-full p-4 flex items-center justify-between text-left"
        onClick={() => setOpen(!open)}
        data-testid="admin-album-emails-toggle"
      >
        <span className="flex items-center gap-2 font-medium text-charcoal">
          <Mail className="w-5 h-5 text-terracotta" /> Accès à l'album — emails autorisés ({emails.length})
        </span>
        {open ? <ChevronUp className="w-5 h-5 text-stone" /> : <ChevronDown className="w-5 h-5 text-stone" />}
      </button>
      {open && (
        <CardContent className="p-4 pt-0">
          <p className="text-xs text-stone mb-3">
            Seuls ces emails peuvent consulter l'album. Retirer un email bloque immédiatement son accès.
          </p>
          <form onSubmit={handleAdd} className="flex gap-2 mb-4">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="nouvel.invite@email.fr"
              className="flex-1"
              data-testid="admin-album-emails-input"
            />
            <Button type="submit" disabled={adding} className="bg-terracotta hover:bg-[#C39365] shrink-0" data-testid="admin-album-emails-add-btn">
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ajouter</span>
            </Button>
          </form>
          <div className="max-h-72 overflow-y-auto divide-y divide-stone/10" data-testid="admin-album-emails-list">
            {emails.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between py-2 gap-2">
                <span className="text-sm text-charcoal break-all">{entry.email}</span>
                <button
                  onClick={() => handleDelete(entry)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-stone hover:text-red-500 hover:bg-red-50 shrink-0"
                  data-testid="admin-album-emails-delete-btn"
                  title="Retirer l'accès"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default function AdminPhotos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [viewPhoto, setViewPhoto] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null); // {ids}
  const [busy, setBusy] = useState(false);
  const [isSuper, setIsSuper] = useState(false);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);

  const load = async () => {
    try {
      const [res, meRes] = await Promise.all([getAdminPhotos(), getAdminMe()]);
      setData(res.data);
      const superAdmin = meRes.data?.role === 'superadmin';
      setIsSuper(superAdmin);
      if (superAdmin) {
        const trashRes = await getPhotosTrash();
        setTrash(trashRes.data?.photos || []);
      }
    } catch (e) {
      toast.error('Erreur lors du chargement des photos');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (photoId) => {
    try {
      await restoreWeddingPhoto(photoId);
      toast.success('Photo restaurée');
      load();
    } catch (e) {
      toast.error('Erreur lors de la restauration');
    }
  };

  useEffect(() => { load(); }, []);

  const toggleAutoPublish = async (checked) => {
    try {
      await updatePhotoSettings(checked);
      setData((d) => ({ ...d, auto_publish: checked }));
      toast.success(checked ? 'Publication automatique activée' : 'Modération activée : les nouvelles photos devront être validées');
    } catch (e) {
      toast.error('Erreur lors de la mise à jour du paramètre');
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === data.photos.length) setSelected(new Set());
    else setSelected(new Set(data.photos.map((p) => p.id)));
  };

  const doBulk = async (action) => {
    setBusy(true);
    try {
      await bulkPhotoAction([...selected], action);
      toast.success(action === 'publish' ? 'Photos publiées' : action === 'hide' ? 'Photos masquées' : 'Photos supprimées');
      setSelected(new Set());
      setDeleteDialog(null);
      load();
    } catch (e) {
      toast.error("Erreur lors de l'action groupée");
    } finally {
      setBusy(false);
    }
  };

  const doSingle = async (photo, action) => {
    try {
      if (action === 'delete') {
        await deleteWeddingPhoto(photo.id);
        toast.success('Photo supprimée');
        setDeleteDialog(null);
        setViewPhoto(null);
      } else {
        const status = action === 'publish' ? 'published' : 'hidden';
        await updatePhotoStatus(photo.id, status);
        toast.success(status === 'published' ? 'Photo publiée' : 'Photo masquée');
        setViewPhoto((v) => (v ? { ...v, status } : v));
      }
      load();
    } catch (e) {
      toast.error("Erreur lors de l'action");
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Photos du mariage">
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-terracotta animate-spin mx-auto" /></div>
      </AdminLayout>
    );
  }

  const { photos, stats, auto_publish } = data;

  return (
    <AdminLayout title="Photos du mariage">
      <div className="space-y-6" data-testid="admin-photos-page">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Photos au total" value={stats.total} icon={Images} />
          <StatCard label="Publiées" value={stats.published} icon={CheckCircle} />
          <StatCard label="En attente" value={stats.pending} icon={Clock} />
          <StatCard
            label="Dernier dépôt"
            value={stats.last_upload_at ? new Date(stats.last_upload_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}
            icon={Download}
          />
        </div>

        {/* Paramètres */}
        <Card className="card-elegant !p-0">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-medium text-charcoal">Publication automatique</p>
              <p className="text-sm text-stone">
                {auto_publish
                  ? 'Les nouvelles photos sont visibles immédiatement dans l\'album.'
                  : 'Les nouvelles photos attendent votre validation avant d\'apparaître.'}
              </p>
            </div>
            <Switch checked={auto_publish} onCheckedChange={toggleAutoPublish} data-testid="admin-photos-autopublish-switch" />
          </CardContent>
        </Card>

        {/* Emails autorisés album */}
        <AlbumEmailsCard />

        {/* QR Code */}
        <Card className="card-elegant !p-0">
          <CardHeader className="p-4 pb-0">
            <CardTitle className="text-base flex items-center gap-2"><QrCode className="w-5 h-5 text-terracotta" /> QR code à imprimer</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-sm text-stone mb-4">
              Ce QR code pointe vers <strong>{window.location.origin}/photos</strong>. Imprimez-le pour les tables, le menu, le bar ou le photobooth.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={photosQrUrl('plain')} download>
                <Button variant="outline" className="w-full sm:w-auto" data-testid="admin-photos-qr-plain-btn">
                  <Download className="w-4 h-4 mr-2" /> QR code seul (HD)
                </Button>
              </a>
              <a href={photosQrUrl('decorated')} download>
                <Button className="btn-primary w-full sm:w-auto !py-2" data-testid="admin-photos-qr-decorated-btn">
                  <Download className="w-4 h-4 mr-2" /> Affiche décorée à imprimer
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Barre d'actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} disabled={photos.length === 0} data-testid="admin-photos-select-all-btn">
            {selected.size === photos.length && photos.length > 0 ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
            {selected.size === photos.length && photos.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
          </Button>
          {photos.length > 0 && (
            <a href={adminPhotosZipUrl()} data-testid="admin-photos-download-all-link">
              <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Tout télécharger (ZIP)</Button>
            </a>
          )}
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto bg-terracotta/10 rounded-xl px-3 py-2" data-testid="admin-photos-bulk-bar">
              <span className="text-sm text-charcoal font-medium">{selected.size} sélectionnée{selected.size > 1 ? 's' : ''}</span>
              <Button size="sm" variant="outline" onClick={() => doBulk('publish')} disabled={busy} data-testid="admin-photos-bulk-publish-btn">
                <Eye className="w-4 h-4 mr-1" /> Publier
              </Button>
              <Button size="sm" variant="outline" onClick={() => doBulk('hide')} disabled={busy} data-testid="admin-photos-bulk-hide-btn">
                <EyeOff className="w-4 h-4 mr-1" /> Masquer
              </Button>
              <a href={adminPhotosZipUrl([...selected])}>
                <Button size="sm" variant="outline" data-testid="admin-photos-bulk-download-btn"><Download className="w-4 h-4 mr-1" /> Télécharger</Button>
              </a>
              {isSuper && (
                <Button size="sm" variant="destructive" onClick={() => setDeleteDialog({ ids: [...selected] })} disabled={busy} data-testid="admin-photos-bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Grille */}
        {photos.length === 0 ? (
          <div className="text-center py-16">
            <Images className="w-12 h-12 text-stone/40 mx-auto mb-4" />
            <p className="text-stone">Aucune photo déposée pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="admin-photos-grid">
            {photos.map((p) => {
              const isSelected = selected.has(p.id);
              const st = STATUS_LABELS[p.status] || STATUS_LABELS.pending;
              return (
                <div key={p.id} className={`relative rounded-xl overflow-hidden group ${isSelected ? 'ring-4 ring-terracotta' : ''}`}>
                  <img
                    src={photoFileUrl(p.id, { thumb: true })}
                    alt={p.original_filename || 'photo'}
                    loading="lazy"
                    className="w-full aspect-square object-cover cursor-pointer"
                    onClick={() => setViewPhoto(p)}
                    data-testid="admin-photos-grid-item"
                  />
                  {p.media_type === 'video' && (
                    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center pointer-events-none">
                      <Play className="w-3.5 h-3.5 text-white fill-white ml-0.5" />
                    </span>
                  )}
                  <button
                    onClick={() => toggleSelect(p.id)}
                    className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-terracotta text-white' : 'bg-black/40 text-white'}`}
                    data-testid="admin-photos-item-select-btn"
                  >
                    {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                  <Badge className={`absolute bottom-2 left-2 text-[10px] ${st.className}`}>{st.label}</Badge>
                </div>
              );
            })}
          </div>
        )}

        {/* Corbeille (superadmin) */}
        {isSuper && (
          <Card className="card-elegant !p-0">
            <button
              className="w-full p-4 flex items-center justify-between text-left"
              onClick={() => setShowTrash(!showTrash)}
              data-testid="admin-photos-trash-toggle"
            >
              <span className="flex items-center gap-2 font-medium text-charcoal">
                <Trash2 className="w-5 h-5 text-stone" /> Corbeille ({trash.length})
              </span>
              {showTrash ? <ChevronUp className="w-5 h-5 text-stone" /> : <ChevronDown className="w-5 h-5 text-stone" />}
            </button>
            {showTrash && (
              <CardContent className="p-4 pt-0">
                {trash.length === 0 ? (
                  <p className="text-sm text-stone">La corbeille est vide. Les photos supprimées y sont conservées 30 jours avant d'être définitivement effacées.</p>
                ) : (
                  <>
                    <p className="text-xs text-stone mb-3">Les photos sont définitivement effacées 30 jours après leur suppression.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="admin-photos-trash-grid">
                      {trash.map((p) => (
                        <div key={p.id} className="relative rounded-xl overflow-hidden">
                          <img src={photoFileUrl(p.id, { thumb: true })} alt="" loading="lazy" className="w-full aspect-square object-cover opacity-60" />
                          <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white p-1.5 flex flex-col gap-1">
                            <span className="text-[10px] text-center">{p.days_left} jour{p.days_left > 1 ? 's' : ''} restant{p.days_left > 1 ? 's' : ''}</span>
                            <Button size="sm" className="h-7 text-xs bg-terracotta hover:bg-[#C39365]" onClick={() => handleRestore(p.id)} data-testid="admin-photos-restore-btn">
                              <RotateCcw className="w-3 h-3 mr-1" /> Restaurer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Vue en grand */}
      <Dialog open={!!viewPhoto} onOpenChange={(o) => !o && setViewPhoto(null)}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4">
          {viewPhoto && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">
                  Photo déposée par {viewPhoto.uploaded_by_name || 'Inconnu'}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {new Date(viewPhoto.created_at).toLocaleString('fr-FR')} — {(viewPhoto.size / 1024 / 1024).toFixed(1)} Mo — {(viewPhoto.likes || []).length} ❤
                </DialogDescription>
              </DialogHeader>
              {viewPhoto.media_type === 'video' ? (
                <video src={photoFileUrl(viewPhoto.id)} poster={photoFileUrl(viewPhoto.id, { thumb: true })} controls playsInline className="w-full max-h-[55vh] object-contain rounded-xl bg-black" />
              ) : (
                <img src={photoFileUrl(viewPhoto.id)} alt="" className="w-full max-h-[55vh] object-contain rounded-xl bg-black/5" />
              )}
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {viewPhoto.status !== 'published' ? (
                  <Button className="w-full sm:w-auto" onClick={() => doSingle(viewPhoto, 'publish')} data-testid="admin-photos-view-publish-btn">
                    <Eye className="w-4 h-4 mr-1" /> Publier
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full sm:w-auto" onClick={() => doSingle(viewPhoto, 'hide')} data-testid="admin-photos-view-hide-btn">
                    <EyeOff className="w-4 h-4 mr-1" /> Masquer
                  </Button>
                )}
                <a href={photoFileUrl(viewPhoto.id, { download: true })} className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full" data-testid="admin-photos-view-download-btn"><Download className="w-4 h-4 mr-1" /> Télécharger</Button>
                </a>
                {isSuper && (
                  <Button variant="destructive" className="w-full sm:w-auto" onClick={() => setDeleteDialog({ ids: [viewPhoto.id], single: viewPhoto })} data-testid="admin-photos-view-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer {deleteDialog?.ids.length > 1 ? `${deleteDialog.ids.length} photos` : 'cette photo'} ?</DialogTitle>
            <DialogDescription>Les photos seront placées dans la corbeille et conservées 30 jours. Vous pourrez les restaurer pendant cette période depuis la section Corbeille.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setDeleteDialog(null)}>Annuler</Button>
            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              disabled={busy}
              onClick={() => (deleteDialog.single ? doSingle(deleteDialog.single, 'delete') : doBulk('delete'))}
              data-testid="admin-photos-confirm-delete-btn"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
